import { useReducer, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import type { ReactionType } from "@nexus/contracts";
import { toast } from "sonner";

import { useAuth } from "@/features/auth/hooks/useAuth";
import type {
  OrderScriptActivityAudience,
  OrderScriptPreflightComment,
  SetOrderNoteReactionPayload,
  SetOrderNoteReactionResult,
} from "@/features/marketplace/types/marketplace.types";
import { ApiError } from "@/lib/api-client";
import i18n from "@/lib/i18n";

import { marketplaceKeys } from "./marketplace-query-keys";

export interface SetOrderNoteReactionArgs {
  tenantId: string;
  orderId: string;
  orderScriptId: string;
  commentId: string;
  reactionType: ReactionType;
  active: boolean;
}

interface CapturedSetOrderNoteReactionArgs extends SetOrderNoteReactionArgs {
  activeTenantIdAtClick: string | null;
}

interface ReactionMutationContext {
  generation: number;
  queryKey: QueryKey;
  previous: OrderScriptPreflightComment[] | undefined;
  pendingKey: string;
}

type SetReactionAdapter = (
  payload: SetOrderNoteReactionPayload,
) => Promise<SetOrderNoteReactionResult>;

const reactionQueues = new Map<string, Promise<unknown>>();

function enqueueReaction<T>(
  streamKey: string,
  work: () => Promise<T>,
): Promise<T> {
  const previous = reactionQueues.get(streamKey) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(work);
  reactionQueues.set(streamKey, next);
  const cleanup = () => {
    if (reactionQueues.get(streamKey) === next) {
      reactionQueues.delete(streamKey);
    }
  };
  void next.then(cleanup, cleanup);
  return next;
}

function reactionStreamKey(
  audience: OrderScriptActivityAudience,
  args: SetOrderNoteReactionArgs,
): string {
  return [
    audience,
    args.tenantId,
    args.orderId,
    args.orderScriptId,
    args.commentId,
    args.reactionType,
  ].join(":");
}

function patchReaction(
  entries: OrderScriptPreflightComment[] | undefined,
  args: SetOrderNoteReactionArgs,
): OrderScriptPreflightComment[] | undefined {
  if (!entries) return entries;
  return entries.map((entry) => {
    if (entry.id !== args.commentId || entry.kind !== "COMMENT") return entry;
    const reactions = entry.reactions ?? [];
    const existing = reactions.find(
      (group) => group.reactionType === args.reactionType,
    );
    if (!existing) {
      if (!args.active) return entry;
      return {
        ...entry,
        reactions: [
          ...reactions,
          {
            reactionType: args.reactionType,
            count: 1,
            isCurrentPersonActive: true,
            actors: [],
          },
        ],
      };
    }
    if (existing.isCurrentPersonActive === args.active) return entry;
    return {
      ...entry,
      reactions: reactions.map((group) =>
        group.reactionType === args.reactionType
          ? {
              ...group,
              count: Math.max(0, group.count + (args.active ? 1 : -1)),
              isCurrentPersonActive: args.active,
            }
          : group,
      ),
    };
  });
}

export function reactionMutationErrorMessage(error: unknown): string {
  return error instanceof ApiError && error.status === 429
    ? i18n.t(
        "marketplace.orders.activity.reactions.rateLimited",
        "Too many reaction changes. Wait a moment and try again.",
      )
    : i18n.t(
        "marketplace.orders.activity.reactions.error",
        "Couldn't update reaction",
      );
}

export function useOrderNoteReactionMutation(
  audience: OrderScriptActivityAudience,
  adapter: SetReactionAdapter,
) {
  const queryClient = useQueryClient();
  const generations = useRef(new Map<string, number>());
  const latestContexts = useRef(new Map<string, ReactionMutationContext>());
  const attemptKeys = useRef(
    new Map<string, { fingerprint: string; idempotencyKey: string }>(),
  );
  const pendingCounts = useRef(new Map<string, number>());
  const [, rerenderPending] = useReducer((value: number) => value + 1, 0);

  const mutation = useMutation<
    SetOrderNoteReactionResult,
    Error,
    CapturedSetOrderNoteReactionArgs,
    ReactionMutationContext
  >({
    mutationFn: (args) => {
      const streamKey = reactionStreamKey(audience, args);
      const fingerprint = JSON.stringify({ active: args.active });
      const previousAttempt = attemptKeys.current.get(streamKey);
      const idempotencyKey =
        previousAttempt?.fingerprint === fingerprint
          ? previousAttempt.idempotencyKey
          : crypto.randomUUID();
      attemptKeys.current.set(streamKey, { fingerprint, idempotencyKey });

      return enqueueReaction(streamKey, async () => {
        const { activeTenantIdAtClick: _activeTenantIdAtClick, ...request } =
          args;
        const result = await adapter({
          ...request,
          audience,
          idempotencyKey,
        });
        const currentAttempt = attemptKeys.current.get(streamKey);
        if (currentAttempt?.idempotencyKey === idempotencyKey) {
          attemptKeys.current.delete(streamKey);
        }
        return result;
      });
    },
    onMutate: async (args) => {
      const queryKey = marketplaceKeys.orderScriptActivity(
        audience,
        args.tenantId,
        args.orderId,
        args.orderScriptId,
      );
      const streamKey = reactionStreamKey(audience, args);
      const generation = (generations.current.get(streamKey) ?? 0) + 1;
      generations.current.set(streamKey, generation);
      pendingCounts.current.set(
        streamKey,
        (pendingCounts.current.get(streamKey) ?? 0) + 1,
      );
      rerenderPending();

      await queryClient.cancelQueries({ queryKey, exact: true });
      const previous =
        queryClient.getQueryData<OrderScriptPreflightComment[]>(queryKey);
      queryClient.setQueryData<OrderScriptPreflightComment[]>(
        queryKey,
        (current) => patchReaction(current, args),
      );
      const context = {
        generation,
        queryKey,
        previous,
        pendingKey: streamKey,
      };
      latestContexts.current.set(streamKey, context);
      return context;
    },
    onError: (error, args, context) => {
      if (!context) return;
      const latest = latestContexts.current.get(context.pendingKey);
      if (latest?.generation !== context.generation) {
        if (latest) latest.previous = context.previous;
        return;
      }
      if (generations.current.get(context.pendingKey) === context.generation) {
        queryClient.setQueryData(context.queryKey, context.previous);
        if (useAuth.getState().activeTenantId === args.activeTenantIdAtClick) {
          toast.error(reactionMutationErrorMessage(error));
        }
      }
    },
    onSettled: (_data, _error, _args, context) => {
      if (!context) return;
      const remaining =
        (pendingCounts.current.get(context.pendingKey) ?? 1) - 1;
      if (remaining > 0)
        pendingCounts.current.set(context.pendingKey, remaining);
      else {
        pendingCounts.current.delete(context.pendingKey);
        void queryClient.invalidateQueries({
          queryKey: context.queryKey,
          exact: true,
        });
      }
      if (
        latestContexts.current.get(context.pendingKey)?.generation ===
        context.generation
      ) {
        latestContexts.current.delete(context.pendingKey);
      }
      rerenderPending();
    },
    meta: { skipGlobalError: true },
  });

  return {
    setReaction: (args: SetOrderNoteReactionArgs) =>
      mutation.mutate({
        ...args,
        activeTenantIdAtClick: useAuth.getState().activeTenantId,
      }),
    setReactionAsync: (args: SetOrderNoteReactionArgs) =>
      mutation.mutateAsync({
        ...args,
        activeTenantIdAtClick: useAuth.getState().activeTenantId,
      }),
    isPending: (args: SetOrderNoteReactionArgs) =>
      (pendingCounts.current.get(reactionStreamKey(audience, args)) ?? 0) > 0,
  };
}
